import React, { useCallback, useMemo } from 'react';

import { defineMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';
import { useRovodal } from '@atlassian/rovodal-context/store';
import { RovodalStoreKeys } from '@atlassian/rovodal-context/store-keys';
import { useActionInvocationStatuses } from '@atlassian/rovodal-context/use-action-invocation-statuses';

import { getRenderMode } from '../../../../common/convertActionStatusToRenderMode';
import { useActionPlanApplyState } from '../../../../controllers/use-action-plan-apply-state';

const acceptAllMessage = defineMessage({
	id: 'ai-mate-jira-admin-agent.jira-admin-agent.canvas.blast-radius.AcceptAllRemainingButton.acceptAll.non-final',
	defaultMessage: 'Apply all remaining changes',
	description:
		'Label for the primary button in the review canvas top bar that applies every plan step that has not yet been applied.',
});

const acceptPlanMessage = defineMessage({
	id: 'ai-mate-jira-admin-agent.jira-admin-agent.canvas.blast-radius.AcceptAllRemainingButton.acceptPlan.non-final',
	defaultMessage: 'Apply changes',
	description:
		'Label for the primary button in the review canvas top bar that applies every plan step.',
});

const completedMessage = defineMessage({
	id: 'ai-mate-jira-admin-agent.jira-admin-agent.canvas.blast-radius.AcceptAllRemainingButton.completed.non-final',
	defaultMessage: 'Done',
	description:
		'Label shown in place of the Accept plan button once a plan step has been successfully applied.',
});

const failedMessage = defineMessage({
	id: 'ai-mate-jira-admin-agent.jira-admin-agent.canvas.blast-radius.AcceptAllRemainingButton.failed.non-final',
	defaultMessage: "Couldn't apply",
	description: 'Message shown next to a plan when applying that plan failed; the user can retry.',
});

const retryMessage = defineMessage({
	id: 'ai-mate-jira-admin-agent.jira-admin-agent.canvas.blast-radius.AcceptAllRemainingButton.retry.non-final',
	defaultMessage: 'Retry',
	description:
		'Label for the button that re-attempts applying a plan after a previous attempt failed.',
});

/**
 * Top-bar action for the blast-radius review canvas: applies every plan step
 * that is still pending (not yet applied, in-flight, or failed) in one batch.
 *
 * Reads the open canvas (actions + resumption deps) from the rovodal store —
 * the same source `IntentStepRow` uses for the per-step Apply — and derives the
 * remaining steps from their live invocation status. Renders a spinner while a
 * batch apply is in flight, a retry affordance if anything errored, and a
 * disabled confirmation once every step has been applied.
 */
export const AcceptAllRemainingButton = (): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [entry] = useRovodal(RovodalStoreKeys.jiraAdminAgentCanvas);

	const state = entry?.state;

	// Only step actions are applyable; the plan action is a container.
	const stepActions = useMemo(
		() => (state?.actions ?? []).filter((action) => action.data.actionType === 'step'),
		[state?.actions],
	);
	const invocationIds = useMemo(
		() => stepActions.map((action) => action.invocationId),
		[stepActions],
	);
	const statuses = useActionInvocationStatuses(state?.conversationId, invocationIds);

	// `useActionPlanApplyState` requires a `conversationId` string; the open canvas may not have
	// one yet, so fall back to `''` the same way `useActionInvocationStatuses` does internally.
	const { perStep, runApply } = useActionPlanApplyState(state?.conversationId ?? '', invocationIds);

	// "Remaining" = steps still valid to apply: stored status still `planned` and live status
	// `idle`. Anything already terminal (`finished`/`cancelled`/`errored`) or in-flight is excluded.
	const remainingActions = useMemo(
		() =>
			stepActions.filter(
				(action) =>
					(action.status ?? 'planned') === 'planned' && statuses[action.invocationId] === 'idle',
			),
		[stepActions, statuses],
	);

	const renderMode = useMemo(
		() =>
			getRenderMode(
				stepActions.map((action) => ({
					storedStatus: action.status ?? 'planned',
					currentStatus: statuses[action.invocationId] ?? 'idle',
					inProgress: perStep[action.invocationId]?.inProgress ?? false,
					error: perStep[action.invocationId]?.error,
				})),
			),
		[stepActions, statuses, perStep],
	);

	const onAcceptAll = useCallback(async () => {
		const { sendMessageConfirmedTools, messageId, conversationId, recipientAgentId } = state ?? {};
		if (!sendMessageConfirmedTools || !messageId) {
			return;
		}

		const idsToApply = remainingActions.map((action) => action.invocationId);

		await runApply(idsToApply, async () => {
			await sendMessageConfirmedTools({
				agentId: recipientAgentId,
				confirmed_tools: {
					message_id: messageId,
					tools: remainingActions.map((action) => ({
						key: action.key,
						tool_id: action.actionId,
						tool_source: action.toolSource,
						invocation_id: action.invocationId,
						resolution: {
							type: 'confirmed',
						},
						data: {
							human_message_id: action.humanMessageId ?? '',
							arguments: action.data,
						},
					})),
				},
				conversationId,
			});
		});
	}, [state, remainingActions, runApply]);

	return (
		<>
			{renderMode === 'IN_PROGRESS' && <Spinner interactionName="load" />}
			{renderMode === 'ERROR' && (
				<Inline space="space.100" alignBlock="center">
					<CrossCircleIcon label="" color={token('color.icon.danger')} />
					<Text size="small" color="color.text.danger">
						{formatMessage(failedMessage)}
					</Text>
					<Button appearance="default" onClick={onAcceptAll}>
						{formatMessage(retryMessage)}
					</Button>
				</Inline>
			)}
			{renderMode === 'COMPLETE' && (
				<Button appearance="subtle" iconBefore={CheckMarkIcon} isDisabled>
					{formatMessage(completedMessage)}
				</Button>
			)}
			{renderMode === 'IDLE' && (
				<Button appearance="primary" onClick={onAcceptAll}>
					{formatMessage(
						remainingActions.length === stepActions.length ? acceptPlanMessage : acceptAllMessage,
					)}
				</Button>
			)}
		</>
	);
};
